// src/components/GridView/hooks/useGridData.ts
import { useState, useEffect, useRef, useCallback } from 'react';
import { gridViewService } from '../service';
import { GRID_PAGE_SIZE } from '../types';
import type { GridViewRequestParams } from '../types';
import type { ConversationDetail } from '@/types/conversation';

// ==================== Normalization ====================
// The grid API returns CustomFieldLayers (not LayerCustomFields) with different
// field names: Name → FieldName, Value → FieldValue.
// We normalize here so the rest of the codebase uses the standard ConversationDetail shape.

function normalizeGridConversation(conv: any): ConversationDetail
{
  const layers: any[] = conv.CustomFieldLayers ?? conv.LayerCustomFields ?? [];
  const layerCustomFields = layers
    .filter((l: any) => Array.isArray(l.CustomFields) && l.CustomFields.length > 0)
    .map((l: any) => ({
      LayerId: l.LayerId,
      LayerName: l.LayerName ?? l.Name,
      Color: l.Color,
      CustomFields: l.CustomFields.map((cf: any) => ({
        CustomFieldValueId: cf.CustomFieldValueId,
        CustomFieldId: cf.CustomFieldId,
        // SelectedValuesText carries the display text for combo/multi-select;
        // for editable types it is null so we fall back to the raw Value.
        FieldValue: cf.SelectedValuesText ?? cf.Value ?? cf.FieldValue,
        // Value keeps the raw IDs — needed for pre-selection in MultiSelectField
        Value: cf.Value,
        FieldName: (cf.Name ?? cf.FieldName ?? '').trim(),
        FieldTypeId: cf.FieldTypeId,
        LayerId: cf.LayerId,
        ConversationId: cf.ConversationId ?? conv.ConversationId,
        ServiceUrl: cf.ServiceUrl,
        TextField: cf.TextField,
        ValueField: cf.ValueField,
        OrderInLayer: cf.OrderInLayer,
        SummatoryValuePhases: cf.SummatoryValuePhases ?? false,
        SummatoryPeoplePhases: cf.SummatoryPeoplePhases ?? false,
      })),
    }));

  return { ...conv, LayerCustomFields: layerCustomFields } as ConversationDetail;
}

interface UseGridDataOptions {
  dashboardId: number;
  configValueId?: number;
  projectId?: string;
  filters?: string;
  searchText?: string;
  layersToHide?: string;
  isActive?: boolean;
  /** PersonId — when set, fetches via GetViewMeDetailInfo (ME View) */
  me?: number;
  extraFilterParams?: Record<string, any>;
}

interface UseGridDataReturn {
  conversations: ConversationDetail[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalItems: number;
  loadMore: () => void;
  refresh: () => void;
  updateField: (
    conversationId: number,
    layerId: number,
    customFieldId: number,
    value: any,
    display: string
  ) => void;
  updateConversationState: (conversationId: number, updates: Partial<ConversationDetail>) => void;
  removeConversation: (conversationId: number) => void;
}

export function useGridData({
  dashboardId,
  configValueId,
  projectId,
  filters,
  searchText,
  layersToHide,
  isActive = true,
  me,
  extraFilterParams,
}: UseGridDataOptions): UseGridDataReturn
{
  const [conversations, setConversations] = useState<ConversationDetail[]>([]);
  // Start as loading=true when active so we show skeleton immediately (avoid flash of empty state)
  const [loading, setLoading] = useState(isActive);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Track ongoing fetch to avoid duplicate calls
  const isFetchingRef = useRef(false);
  // Track loaded count to compute hasMore without stale closure issues
  const loadedCountRef = useRef(0);

  const buildParams = useCallback(
    (page: number): GridViewRequestParams => ({
      CustomFields: [],
      PageNumber: page,
      PageSize: GRID_PAGE_SIZE,
      DashboardId: dashboardId,
      Project: projectId ?? '',
      Filters: filters ?? '',
      SearchText: searchText ?? '',
      LayersToHide: layersToHide ?? '',
      DashboardViewTypeId: me != null ? 11 : 4,
      IsFilterPersonal: false,
      ...(configValueId != null && { ConfigValueId: configValueId }),
      ...(me != null && { Me: me }),
      ...extraFilterParams,
    }),
    [
      dashboardId,
      configValueId,
      projectId,
      filters,
      searchText,
      layersToHide,
      me,
      extraFilterParams,
    ],
  );

  const fetchInitial = useCallback(async () =>
  {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);

    try
    {
      const params = buildParams(1);
      const response = await (me != null
        ? gridViewService.getMeViewConversations(params)
        : gridViewService.getGridConversations(params));
      const items = (response.Conversations ?? [])
        .map(normalizeGridConversation)
        .filter(c => !(projectId && c.ConfigValueId === 9));

      // GetViewMeDetailInfo has no TotalItems — keep paging until a page is empty
      const total = response.TotalItems ?? 0;
      const computedHasMore =
        total > 0
          ? items.length < total
          : me != null
            ? items.length > 0
            : items.length >= GRID_PAGE_SIZE;

      setConversations(items);
      setTotalItems(total);
      setPageNumber(1);
      loadedCountRef.current = items.length;
      setHasMore(computedHasMore);
    }
    finally
    {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [buildParams]);

  const loadMore = useCallback(async () =>
  {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    setLoadingMore(true);

    try
    {
      const nextPage = pageNumber + 1;
      const params = buildParams(nextPage);
      const response = await (me != null
        ? gridViewService.getMeViewConversations(params)
        : gridViewService.getGridConversations(params));
      const newItems = (response.Conversations ?? [])
        .map(normalizeGridConversation)
        .filter(c => !(projectId && c.ConfigValueId === 9));

      // No new items → definitively at the end
      if (newItems.length === 0)
      {
        setHasMore(false);
        return;
      }

      setConversations(prev => [...prev, ...newItems]);
      loadedCountRef.current += newItems.length;

      const responseTotal = response.TotalItems ?? 0;
      const computedHasMore =
        responseTotal > 0
          ? loadedCountRef.current < responseTotal
          : me != null
            ? true // keep paging until empty
            : newItems.length >= GRID_PAGE_SIZE; // full page → assume more

      setTotalItems(responseTotal);
      setPageNumber(nextPage);
      setHasMore(computedHasMore);
    }
    finally
    {
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [buildParams, hasMore, pageNumber, totalItems]);

  // Fetch whenever isActive becomes true or key params change
  useEffect(() =>
  {
    if (!isActive) return;
    fetchInitial();
  }, [
    isActive,
    dashboardId,
    configValueId,
    projectId,
    filters,
    searchText,
    layersToHide,
    me,
    extraFilterParams,
  ]);

  // Update a single custom field value in memory so re-opening the editor shows the saved value
  const updateField = useCallback(
    (
      conversationId: number,
      layerId: number,
      customFieldId: number,
      value: any,
      display: string,
    ) =>
    {
      setConversations(prev =>
        prev.map(conv =>
        {
          if (conv.ConversationId !== conversationId) return conv;
          return {
            ...conv,
            LayerCustomFields: (conv.LayerCustomFields ?? []).map(layer =>
            {
              if (layer.LayerId !== layerId) return layer;
              return {
                ...layer,
                CustomFields: (layer.CustomFields ?? []).map(cf =>
                {
                  if (cf.CustomFieldId !== customFieldId) return cf;
                  return { ...cf, Value: value, FieldValue: display };
                }),
              };
            }),
          };
        }),
      );
    },
    [],
  );

  const updateConversationState = useCallback(
    (conversationId: number, updates: Partial<ConversationDetail>) =>
    {
      setConversations(prev =>
        prev.map(c => (c.ConversationId === conversationId ? { ...c, ...updates } : c)),
      );
    },
    [],
  );

  const removeConversation = useCallback((conversationId: number) =>
  {
    setConversations(prev => prev.filter(c => c.ConversationId !== conversationId));
  }, []);

  return {
    conversations,
    loading,
    loadingMore,
    hasMore,
    totalItems,
    loadMore,
    refresh: fetchInitial,
    updateField,
    updateConversationState,
    removeConversation,
  };
}
